import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Checkbox,
  FormGroup,
  FormHelperText,
} from '@mui/material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { VerifyRecord } from '../../models/common';
import IconButton from '@mui/material/IconButton';
// 在顶部添加新的导入
import JSZip from 'jszip';
import DownloadIcon from '@mui/icons-material/Download';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import CloseIcon from '@mui/icons-material/Close';

// 错误类型定义
const ERROR_TYPES = {
  NAME_ERROR: 'e0001',
  ID_NUMBER_ERROR: 'e0002',
  ID_CARD_FRONT_ERROR: 'e0003',
  ID_CARD_BACK_ERROR: 'e0004',
  DRIVING_LICENSE_ERROR: 'e0005',
  CERTIFICATE_ERROR: 'e0006'
};

// 错误类型映射
const ERROR_TYPE_LABELS: Record<string, string> = {
  [ERROR_TYPES.NAME_ERROR]: '姓名错误',
  [ERROR_TYPES.ID_NUMBER_ERROR]: '身份证号错误',
  [ERROR_TYPES.ID_CARD_FRONT_ERROR]: '身份证正面错误',
  [ERROR_TYPES.ID_CARD_BACK_ERROR]: '身份证反面错误',
  [ERROR_TYPES.DRIVING_LICENSE_ERROR]: '驾驶证错误',
  [ERROR_TYPES.CERTIFICATE_ERROR]: '资格证错误'
};

interface VerifyEditDialogProps {
  open: boolean;
  onClose: () => void;
  verifyId: string;
  verifyData: VerifyRecord | null;
}
// 添加图片预览状态接口
interface ImagePreviewState {
  src: string | null;
  rotation: number;
  scale: number;
}

export const VerifyEditDialog: React.FC<VerifyEditDialogProps> = ({
  open,
  onClose,
  verifyId,
  verifyData,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const [idCardFrontImage, setIdCardFrontImage] = useState<string>();
  const [idCardBackImage, setIdCardBackImage] = useState<string>();
  const [drivingLicenseImage, setDrivingLicenseImage] = useState<string>();
  const [certificate, setCertificate] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [verifyStatus, setVerifyStatus] = useState<string>('1'); // 1: 通过, -1: 拒绝
  const [rejectReason, setRejectReason] = useState<string>('');
  const [selectedErrorTypes, setSelectedErrorTypes] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // 添加新的状态
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
  // 修改图片预览状态
  const [preview, setPreview] = useState<ImagePreviewState>({
    src: null,
    rotation: 0,
    scale: 1,
  });
  // 添加图片控制函数
  const handleRotateLeft = () => {
    setPreview(prev => ({
      ...prev,
      rotation: (prev.rotation - 90) % 360,
    }));
  };

  const handleRotateRight = () => {
    setPreview(prev => ({
      ...prev,
      rotation: (prev.rotation + 90) % 360,
    }));
  };

  const handleZoomIn = () => {
    setPreview(prev => ({
      ...prev,
      scale: Math.min(prev.scale + 0.5, 3),
    }));
  };

  const handleZoomOut = () => {
    setPreview(prev => ({
      ...prev,
      scale: Math.max(prev.scale - 0.5, 0.5),
    }));
  };

  const handleClosePreview = () => {
    setPreview({ src: null, rotation: 0, scale: 1 });
  };

  useEffect(() => {
    const fetchImages = async () => {
      if (!verifyId) return;

      try {
        setLoading(true);
        const imagesData = await dataProvider.getVerifyImages(verifyId);
        const face = await dataProvider.getVerifyImageData(imagesData.face);
        const emblem = await dataProvider.getVerifyImageData(imagesData.emblem);
        const driverLicense = await dataProvider.getVerifyImageData(imagesData.driver_license);
        const certificate = await dataProvider.getVerifyImageData(imagesData.certificate);
        setIdCardFrontImage(face);
        setIdCardBackImage(emblem);
        setDrivingLicenseImage(driverLicense);
        setCertificate(certificate);
      } catch (error) {
        console.error('获取认证图片失败:', error);
      } finally {
        setLoading(false);
      }
    };

    if (open && verifyId) {
      fetchImages();
    }
  }, [open, verifyId, dataProvider]);

  const getImageTitle = (type: string) => {
    switch (type) {
      case 'idcard_front':
        return '身份证正面照';
      case 'idcard_back':
        return '身份证反面照';
      case 'driving_license':
        return '驾驶证照片';
      case 'qualification':
        return '资格证照片';
      default:
        return '其他证件照';
    }
  };

  const handleSubmit = async () => {
    if (verifyStatus === '-1') {
      // 检查是否至少选择了一个错误类型
      if (selectedErrorTypes.length === 0) {
        notify('请至少选择一个错误类型', { type: 'error' });
        return;
      }
      
      if (!rejectReason) {
        notify('请填写拒绝原因', { type: 'error' });
        return;
      }
    }

    try {
      setSubmitting(true);
      // 准备错误类型信息
      const errorTypesList: string[] = [];
      if (verifyStatus === '-1') {
        // 将选中的错误类型代码转换为对应的文本描述
        selectedErrorTypes.forEach(typeCode => {
          // const label = ERROR_TYPE_LABELS[typeCode];
          // if (label) {
          //   errorTypesList.push(`${label}(${typeCode})`);
          // }
          errorTypesList.push(`${typeCode}`);
        });
      }
      
      // 组合错误类型和拒绝原因
      const fullReason = verifyStatus === '-1' 
        ? `拒绝原因：${rejectReason};errorCodes=${errorTypesList.join('、')}` 
        : undefined;
      console.log('fullReason', fullReason);
      await dataProvider.modifyUserStatus(verifyId, {
        status: parseInt(verifyStatus),
        comment: fullReason,
      });
      notify('更新成功', { type: 'success' });
      refresh();
      onClose();
    } catch (error) {
      notify('更新失败', { type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // 获取图片名称的辅助函数
  const getImageName = (index: number): string => {
    switch (index) {
      case 0:
        return '身份证正面';
      case 1:
        return '身份证背面';
      case 2:
        return '驾驶证';
      case 3:
        return '从业资格证';
      default:
        return '未知图片';
    }
  };
  // 修改图片容器组件
  const ImageContainer = ({ src, alt, title }: { src: string; alt: string; title: string }) => (
    <Box position="relative">
      <img
        src={src}
        alt={alt}
        style={{ width: '100%', maxHeight: '200px', objectFit: 'contain' }}
      />
      <IconButton
        onClick={() => setPreview({ src, rotation: 0, scale: 1 })}
        sx={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
          },
        }}
        size="small"
      >
        <ZoomInIcon />
      </IconButton>
    </Box>
  );


  // 添加下载处理函数
  const handleDownloadImages = async () => {
    const allImages = [idCardFrontImage, idCardBackImage, drivingLicenseImage, certificate].filter(Boolean);
    if (allImages.length === 0) return;

    try {
      const zip = new JSZip();
      const zipFileName = `${verifyData?.name ?? '未知姓名'}_${verifyData?.idNo ?? '无证件号码'}.zip`;

      // 添加图片到zip
      const imagePromises = allImages.map(async (imageUrl, index) => {
        const response = await fetch(imageUrl ?? "");
        const blob = await response.blob();
        const imageName = `${index + 1}_${getImageName(index)}.jpg`;
        zip.file(imageName, blob);
      });

      await Promise.all(imagePromises);

      // 生成并下载zip文件
      const content = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('下载图片失败:', error);
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography>认证详情</Typography>
            <IconButton
              onClick={handleDownloadImages}
              disabled={loading}
              title="下载所有图片"
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom>基本信息</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography>姓名：{verifyData?.data?.identityName}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>身份证号：{verifyData?.data?.identityNumber}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography>手机号：{verifyData?.phone}</Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box mb={3}>
                <Typography variant="subtitle1" gutterBottom>证件照片</Typography>
                <Grid container spacing={2}>
                  {idCardFrontImage && (
                    <Grid item xs={6}>
                      <Typography variant="body2" gutterBottom>
                        {getImageTitle('idcard_front')}
                      </Typography>
                      <ImageContainer
                        src={idCardFrontImage}
                        alt="身份证正面"
                        title={getImageTitle('idcard_front')}
                      />
                    </Grid>
                  )}
                  {idCardBackImage && (
                    <Grid item xs={6}>
                      <Typography variant="body2" gutterBottom>
                        {getImageTitle('idcard_back')}
                      </Typography>
                      <ImageContainer
                        src={idCardBackImage}
                        alt="身份证反面"
                        title={getImageTitle('idcard_back')}
                      />
                    </Grid>
                  )}
                  {drivingLicenseImage && (
                    <Grid item xs={6}>
                      <Typography variant="body2" gutterBottom>
                        {getImageTitle('driving_license')}
                      </Typography>
                      <ImageContainer
                        src={drivingLicenseImage}
                        alt="行驶证"
                        title={getImageTitle('driving_license')}
                      />
                    </Grid>
                  )}
                  {certificate && (
                    <Grid item xs={6}>
                      <Typography variant="body2" gutterBottom>
                        {getImageTitle('qualification')}
                      </Typography>
                      <ImageContainer
                        src={certificate}
                        alt="资格证"
                        title={getImageTitle('qualification')}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </>
          )}
          <Box mt={3}>
            <Typography variant="subtitle1" gutterBottom>审核结果</Typography>
            <RadioGroup
              value={verifyStatus}
              onChange={(e) => {
                setVerifyStatus(e.target.value);
                if (e.target.value === '1') {
                  setRejectReason('');
                  setSelectedErrorTypes([]);
                }
              }}
            >
              <FormControlLabel value="1" control={<Radio />} label="通过" />
              <FormControlLabel value="-1" control={<Radio />} label="拒绝" />
            </RadioGroup>

            {verifyStatus === '-1' && (
              <>
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>错误类型（至少选择一项）</Typography>
                  <FormGroup>
                    <Grid container spacing={2}>
                      {Object.entries(ERROR_TYPES).map(([key, typeCode]) => (
                        <Grid item xs={4} key={typeCode}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedErrorTypes.includes(typeCode)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedErrorTypes([...selectedErrorTypes, typeCode]);
                                  } else {
                                    setSelectedErrorTypes(selectedErrorTypes.filter(type => type !== typeCode));
                                  }
                                }}
                              />
                            }
                            label={`${ERROR_TYPE_LABELS[typeCode]}`}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </FormGroup>
                  {verifyStatus === '-1' && selectedErrorTypes.length === 0 && (
                    <FormHelperText error>请至少选择一个错误类型</FormHelperText>
                  )}
                </Box>
                <TextField
                  fullWidth
                  label="拒绝原因"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  margin="normal"
                  multiline
                  rows={3}
                  required
                  error={verifyStatus === '-1' && !rejectReason}
                  helperText={verifyStatus === '-1' && !rejectReason ? '请填写拒绝原因' : ''}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>取消</Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={submitting || (verifyStatus === '-1' && (!rejectReason || selectedErrorTypes.length === 0))}
          >
            {submitting ? <CircularProgress size={24} /> : '提交'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* 添加图片预览 Dialog */}
      <Dialog
        open={!!preview.src}
        onClose={handleClosePreview}
        maxWidth={false}
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative', bgcolor: 'black' }}>
          {preview.src && (
            <>
              <img
                src={preview.src}
                alt="预览图"
                style={{
                  width: '100%',
                  height: '90vh',
                  objectFit: 'contain',
                  transform: `rotate(${preview.rotation}deg) scale(${preview.scale})`,
                  transition: 'transform 0.3s ease',
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  display: 'flex',
                  gap: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  padding: 1,
                  borderRadius: 1,
                }}
              >
                <IconButton onClick={handleZoomIn} size="small" sx={{ color: 'white' }}>
                  <ZoomInIcon />
                </IconButton>
                <IconButton onClick={handleZoomOut} size="small" sx={{ color: 'white' }}>
                  <ZoomOutIcon />
                </IconButton>
                <IconButton onClick={handleRotateLeft} size="small" sx={{ color: 'white' }}>
                  <RotateLeftIcon />
                </IconButton>
                <IconButton onClick={handleRotateRight} size="small" sx={{ color: 'white' }}>
                  <RotateRightIcon />
                </IconButton>
                <IconButton onClick={handleClosePreview} size="small" sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VerifyEditDialog;